import { generateRecurringBookingConfirmPaymentDTO, generateRecurringOrderBookingsDTO } from 'components/RecurringBookings'
import { AttendeeItem, AttendeeListItemApiResponse } from 'types/attendee'
import { BookingApiOutput } from 'types/booking'
import { calculateCouponTotalAmount, CouponApiResponse } from 'types/coupon'
import { DiscountType } from 'types/discount'
import { IntakeClientResponse, mapClientResponseToDTO } from 'types/intake-forms'
import { Order, OrderType } from 'types/order'
import { BookingConfirmPaymentWithoutBookingDTO, BookingDTO, OrderBookingWithoutBookingDTO, OrderDTO, RecurringBookingDates } from 'types/order-booking'
import { PaymentMethod } from 'types/payment'
import { getApiRequest, postApiRequest } from 'utils/fetch-helper'
import { denormalizeOrder } from './order'

export type BookingPaymentDto = {
    amount: number
    paymentMethodId?: string
    clientEmail: string
    clientFirstName: string
    clientLastName: string
    clientPhone: string
    tenantId: string
    locationId: string
    groupClassAvailabilityId?: string
    couponCode?: string
    couponClientId?: string
    client_responses?: IntakeClientResponse[]
}

export function useBookingService() {
    //  TODO: Investigate why the GViewItem is re-rendering when using useApiRequest
    // const { getApiRequest } = useApiRequest()

    const getGroupClassAvailabilityBookings = async function (
        availability_id: string
    ) {
        const results: Record<string, string> = {}
        const bookings_xhr = await getApiRequest(
            `/v1/bookings/group-class-availability-id/?groupClassAvailabilityId=${availability_id}`
        )

        if (bookings_xhr?.message) return results && bookings_xhr

        const bookings: Record<string, string>[] = bookings_xhr.map(
            (booking: BookingApiOutput) => {
                return {
                    client_id: booking.clientId,
                    cognito_id: booking.clientCognitoId,
                    first_name: booking.clientFirstName,
                    last_name: booking.clientLastName,
                    booking_id: booking.id,
                    order_id: booking.orderId,
                    notes: booking.notes,
                    signed_in_at: booking.signedInAt,
                }
            }
        )

        return { bookings }
    }

    const getGroupClassAvailabilityAttendees = async function (
        availability_id: string
    ) {
        const { attendees, instructor } = await getApiRequest(
            `/v1/bookings/attendees/?groupClassAvailabilityId=${availability_id}`
        )
        const normalized_attendees: AttendeeItem[] = attendees?.map(
            ({
                attendee,
                bookingId,
                order: order_api_output,
            }: AttendeeListItemApiResponse) => {
                const order: Order = denormalizeOrder(order_api_output)
                return {
                    ...attendee,
                    booking_id: bookingId,
                    order,
                }
            }
        )
        return {
            attendees: normalized_attendees,
            instructor,
        }
    }

    return {
        getGroupClassAvailabilityBookings,
        getGroupClassAvailabilityAttendees,
    }
}

export async function payAndBookForClass(
    dto: BookingPaymentDto,
    savePaymentMethod = false,
    is_recurring: boolean = false,
    recurring_dates?: RecurringBookingDates[],
    duration?: number,
    valid_recurring_count: number = 1,
    coupon?: CouponApiResponse //TODO: map this value whereever it's coming from
) {
    const {
        amount,
        clientEmail,
        clientFirstName,
        clientLastName,
        clientPhone,
        couponCode,
        groupClassAvailabilityId,
        locationId,
        paymentMethodId,
        tenantId,
        client_responses
    } = dto

    savePaymentMethod =
        savePaymentMethod || localStorage.getItem('save_card') == 'true'
    let total_amount
    if (coupon) {
        total_amount = calculateCouponTotalAmount(amount, coupon, valid_recurring_count)
    } else {
        total_amount = amount * valid_recurring_count
    }
    const paymentMethod = total_amount > 0 ? 'CARD' : ''

    if (is_recurring) {
        return await postApiRequest('/v1/payments/confirm-booking-payment-from-list', generateRecurringBookingConfirmPaymentDTO({
            amount: total_amount,
            order: {
                clientEmail,
                clientFirstName,
                clientLastName,
                clientPhone,
                tenantId,
                paymentMethod,
                paymentMethodId,
                type: OrderType.SERVICE,
                couponCode,
            } as OrderDTO,
            tenantId,
            savePaymentMethod,
            paymentMethodId
        } as BookingConfirmPaymentWithoutBookingDTO,
            {
                clientEmail,
                tenantId,
                groupClassAvailabilityId
            } as BookingDTO, recurring_dates || [], duration || 0, client_responses))
    } else {
        return await postApiRequest(
            '/v1/payments/confirm-booking-payment',
            {
                amount: total_amount,
                paymentMethodId,
                tenantId,
                locationId,
                savePaymentMethod,
                clientEmail,
                order: {
                    clientEmail,
                    clientFirstName,
                    clientLastName,
                    clientPhone,
                    couponCode,
                    tenantId,
                    paymentMethod,
                    paymentMethodId,
                    type: OrderType.SERVICE,
                },
                booking: {
                    groupClassAvailabilityId,
                    clientEmail,
                    paymentMethodId,
                    tenantId,
                },
                responses: client_responses?.map(mapClientResponseToDTO)
            }
        )
    }
}

export async function payAndBookForAppointment(
    amount: number,
    paymentMethodId: string,
    clientEmail: string,
    clientFirstName: string,
    clientLastName: string,
    clientPhone: string,
    tenantId: string,
    locationId: string,
    appointmentDto: Record<string, unknown>
) {
    const savePaymentMethod = localStorage.getItem('save_card') == 'true'
    const paymentMethod = amount > 0 ? 'CARD' : ''

    return await postApiRequest('/v1/payments/confirm-booking-payment', {
        amount,
        paymentMethod,
        paymentMethodId,
        tenantId,
        locationId,
        savePaymentMethod: savePaymentMethod,
        clientEmail,
        order: {
            clientEmail,
            clientFirstName,
            clientLastName,
            clientPhone,
            tenantId,
            paymentMethod,
            paymentMethodId,
            type: OrderType.SERVICE,
        },
        booking: {
            appointmentDto,
            clientEmail,
            paymentMethodId,
            tenantId,
        },
    })
}

export async function bookForClassUsingMembership(
    membershipClientId: string,
    clientEmail: string,
    clientFirstName: string,
    clientLastName: string,
    clientPhone: string,
    tenantId: string,
    groupClassAvailabilityId: string,
    is_recurring?: boolean,
    recurring_dates?: RecurringBookingDates[],
    duration?: number,
    client_responses?: IntakeClientResponse[]

) {
    if (is_recurring) {
        return await postApiRequest(
            '/v1/orders/order-booking-from-list',
            generateRecurringOrderBookingsDTO(
                {
                    order: {
                        clientEmail,
                        clientFirstName,
                        clientLastName,
                        clientPhone,
                        tenantId,
                        membershipClientId,
                        type: OrderType.SERVICE,
                        couponCode: '',
                        fullyPaid: false,
                    } as OrderDTO,
                } as OrderBookingWithoutBookingDTO,
                {
                    clientEmail,
                    tenantId,
                    groupClassAvailabilityId,
                } as BookingDTO,
                recurring_dates || [],
                duration || 0,
                client_responses
            )
        )
    } else {
        return await postApiRequest('/v1/orders/order-booking', {
            order: {
                clientEmail,
                clientFirstName,
                clientLastName,
                clientPhone,
                tenantId,
                membershipClientId,
                type: OrderType.SERVICE,
                fullyPaid: false,
                payLater: (localStorage.getItem('payment_method') as string == '"LATER"') //TODO: really should refactor this
            },
            booking: {
                groupClassAvailabilityId,
                clientEmail,
                tenantId,
            },
            responses: client_responses?.map(mapClientResponseToDTO)
        })
    }
}

export async function bookForClassUsingFreeAppointment(
    membershipClientId: string,
    clientEmail: string,
    clientFirstName: string,
    clientLastName: string,
    clientPhone: string,
    tenantId: string,
    groupClassAvailabilityId: string,
) {
    return await postApiRequest('/v1/orders/order-booking', {
        order: {
            adhocDiscount: 100,
            adhocDiscountType: DiscountType.PERCENT,
            clientEmail,
            clientFirstName,
            clientLastName,
            clientPhone,
            tenantId,
            membershipClientId,
            type: OrderType.SERVICE,
            fullyPaid: false,
        },
        booking: {
            groupClassAvailabilityId,
            clientEmail,
            tenantId,
        },
    })
}
